import React, { useCallback, useEffect, useRef, useState } from "react";
import AuthenticationService from "../../services/AuthenticationService";
import { clearCache, getCache, setCache } from "../../utils/PersistenceUtils";
import LogonModal from "../account/LogonModal";
import Loading from "./components/Loading";
import ModalService from "./bootstrap/modal/Modal";
import FloatingPanelService from "./FloatingPanel";

export const AUTHENTICATION_DATA_STORAGE_KEY = "authentication_data";

/**
 * Rutas públicas del portal que no requieren autenticación de empresa.
 * Estas rutas usan su propio token de cliente y no deben mostrar el modal de login
 * cuando la sesión de empresa expira.
 */
const PUBLIC_PORTAL_ROUTES = ["portalclient", "tk"] as const;

export interface IAuthenticationData {
  token: string;
  expires: number;
  refreshToken: string;
  refreshExpires: number;
}

type IAuthenticationContext = {
  setCurrentAuthentication: (value: IAuthenticationData) => void;
  stopExpirationTimeout: () => void;
  isAuthenticated: boolean;
  doLogout: () => void;
};

export let setAuthentication = (value: IAuthenticationData) =>
  window.clearImmediate(undefined);
const AuthenticationContext = React.createContext<IAuthenticationContext>(
  {} as IAuthenticationContext
);
let sessionExpirationTimeout: ReturnType<typeof setTimeout>;
const AuthenticationProvider = (props: React.PropsWithChildren<object>) => {
  const [authStatus, setAuthStatus] = useState<
    "Needs initalization" | "Not logged in" | "Expired" | "Authenticated"
  >("Needs initalization");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Detecta si la ruta actual es una ruta pública del portal.
   * Las rutas públicas del portal usan su propio token de cliente y no deben
   * mostrar el modal de login cuando la sesión de empresa expira.
   *
   * Las rutas tienen el formato: /:lang/portalclient o /:lang/tk
   * donde :lang puede ser es, en, pt, etc.
   *
   * @returns true si estamos en una ruta pública del portal, false en caso contrario
   */
  const isPublicPortalRoute = useCallback(() => {
    const pathname = window.location.pathname.toLowerCase();
    // Construir el patrón regex dinámicamente usando la constante PUBLIC_PORTAL_ROUTES
    // Esto permite agregar nuevas rutas públicas fácilmente sin modificar el regex
    const routesPattern = PUBLIC_PORTAL_ROUTES.join("|");
    // Patrón regex para detectar rutas públicas del portal
    // Formato: /:lang/portalclient o /:lang/tk (donde :lang puede ser es, en, pt, etc.)
    // El patrón verifica que la ruta esté en un segmento válido para evitar falsos positivos
    // Permite query params (?), hash (#) y trailing slash, pero no subdirectorios
    const publicPortalRoutePattern = new RegExp(
      `^\\/(es|en|pt)\\/(${routesPattern})(\\/?$|\\?|#)`
    );
    return publicPortalRoutePattern.test(pathname);
  }, []);

  function stopExpirationTimeout(): void {
    clearTimeout(sessionExpirationTimeout);
  }

  function setCurrentAuthentication(value: IAuthenticationData) {
    setCache(AUTHENTICATION_DATA_STORAGE_KEY, value);
    setupExpiration();
    refreshTokenAuth();
  }

  function getExpirationTimeMs(expirationTime: number | undefined) {
    return (expirationTime ?? 0) * 1000 - Date.now();
  }

  const setupExpiration = useCallback(() => {
    clearTimeout(sessionExpirationTimeout);
    sessionExpirationTimeout = setTimeout(async () => {
      const authenticationData = getCache<IAuthenticationData>(
        AUTHENTICATION_DATA_STORAGE_KEY
      );
      const expirationTime = getExpirationTimeMs(
        authenticationData?.refreshExpires
      );

      if (expirationTime <= 0) {
        setAuthStatus("Expired");
      } else {
        setupExpiration();
      }
    }, 1000 * 5);
  }, []);

  function doLogout(): void {
    clearCache();
    stopExpirationTimeout();
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setAuthStatus("Not logged in");
    ModalService.hideModal();
    FloatingPanelService.hidePanel();
  }

  const value: IAuthenticationContext = {
    setCurrentAuthentication,
    stopExpirationTimeout,
    isAuthenticated: authStatus !== "Not logged in",
    doLogout
  };

  const refreshTokenAuth = useCallback(() => {
    const authenticationData = getCache<IAuthenticationData>(
      AUTHENTICATION_DATA_STORAGE_KEY
    );
    const expirationTime = getExpirationTimeMs(
      authenticationData?.refreshExpires
    );
    if (expirationTime > 0) {
      setupExpiration();
      setAuthStatus("Authenticated");
    } else {
      setAuthStatus("Not logged in");
    }
  }, [setupExpiration]);

  useEffect(() => {
    AuthenticationService.getAuthenticationToken().then(() => {
      refreshTokenAuth();
    });
  }, [refreshTokenAuth, setupExpiration]);

  useEffect(() => {
    setAuthentication = setCurrentAuthentication;
  });

  useEffect(() => {
    if (authStatus === "Authenticated") {
      intervalRef.current = setInterval(() => {
        const authenticationData = getCache<IAuthenticationData>(
          AUTHENTICATION_DATA_STORAGE_KEY
        );
        if (authenticationData) {
          const expirationTime = getExpirationTimeMs(
            authenticationData.refreshExpires
          );
          if (expirationTime < 0) {
            setAuthStatus("Expired");
          }
        }
      }, 20 * 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [authStatus]);

  // Solo mostrar el modal de login si la sesión expiró Y no estamos en una ruta pública del portal
  // Las rutas públicas del portal usan su propio token de cliente y no deben verse afectadas
  // por la expiración de la sesión de empresa
  const shouldShowLoginModal =
    authStatus === "Expired" && !isPublicPortalRoute();

  return (
    <AuthenticationContext.Provider value={value}>
      <LogonModal expired={shouldShowLoginModal} />
      {authStatus === "Needs initalization" ? <Loading /> : props.children}
    </AuthenticationContext.Provider>
  );
};

export { AuthenticationProvider };
export default AuthenticationContext;
